// Shared helpers for reading the user's current (main) subscription.
// Billing is subscription-only (prepaid credit has been removed).

import moment from "moment";

// Page allowance bundled with each plan (mirrors the plan feature copy).
export const PLAN_PAGE_ALLOWANCE = {
  starter: 150,
  business: 1000,
};

export const PLAN_DISPLAY_NAME = {
  starter: "Starter",
  business: "Business",
};

export const getMainSubscription = (user) => user?.main_subscription || null;

export const getCurrentPlanCode = (user) =>
  user?.main_subscription?.plan?.code || null;

export const hasActiveSubscription = (user) => {
  const sub = getMainSubscription(user);
  return Boolean(
    sub && sub.status === "active" && !sub.cancel_at_period_end
  );
};

export const isSubscriptionCanceling = (user) => {
  const sub = getMainSubscription(user);
  return Boolean(
    sub &&
      sub.status !== "canceled" &&
      (sub.cancel_at_period_end === true ||
        sub.cancel_at_period_end === 1 ||
        sub.cancel_at)
  );
};

export const getPlanName = (user) => {
  const sub = getMainSubscription(user);
  const code = getCurrentPlanCode(user);
  return sub?.plan?.name || PLAN_DISPLAY_NAME[code] || code || "—";
};

export const getPlanPageAllowance = (user) =>
  PLAN_PAGE_ALLOWANCE[getCurrentPlanCode(user)] ?? null;

// Live page-usage meter for the current billing period, computed by the API
// (/me → page_usage). Shape: { allowance, used, remaining, period_start, ... }.
export const getPageUsage = (user) => user?.page_usage || null;

// Prefer the allowance the API reports alongside usage; fall back to the static
// per-plan map when usage isn't available yet.
export const getPageAllowance = (user) => {
  const usage = getPageUsage(user);
  return typeof usage?.allowance === "number"
    ? usage.allowance
    : getPlanPageAllowance(user);
};

export const getPagesUsed = (user) => {
  const usage = getPageUsage(user);
  return typeof usage?.used === "number" ? usage.used : null;
};

export const getPagesRemaining = (user) => {
  const usage = getPageUsage(user);
  return typeof usage?.remaining === "number" ? usage.remaining : null;
};

// The API serves current_period_end as a SQL DATE (ISO string), but Stripe
// webhooks/tests may still hand us a raw unix-seconds timestamp. Handle both:
// a finite number is treated as unix seconds, anything else as a date string.
export const getRenewalDate = (user) => {
  const end = getMainSubscription(user)?.current_period_end;
  if (end == null) return null;
  const parsed =
    typeof end === "number" || /^\d+$/.test(String(end))
      ? moment.unix(Number(end))
      : moment(end);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : null;
};
